import React, { useEffect } from 'react'
import {
  Button, List, Switch, Modal, Space, Spin, Typography,
} from 'antd'
import {
  PlusCircleOutlined, EditOutlined, DeleteOutlined, ExclamationCircleFilled,
  ArrowUpOutlined, ArrowDownOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '~/Store'
import {
  getCarouselItems,
  getIsLoading,
  getModalVisibility,
  LoginBrandingActions,
  ModalName,
} from '~/Store/LoginBranding'
import { ILoginCarouselItem } from '~/Services/LoginBranding.interface'
import { resolveIdentityAssetUrl } from '~/Services'
import { NoData } from '~/Components/NoData'
import { LoginCarouselItemFormModal } from './LoginCarouselItemFormModal'

const { confirm } = Modal

// Nhúng trong ProductFormModal khi Edit 1 application đã tồn tại (giống logo — cần applicationId thật,
// không hiển thị khi đang Create). Load/refetch tự quản lý theo applicationId, không phụ thuộc parent.
export const LoginBrandingSection: React.FC<{ applicationId: string }> = ({ applicationId }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const items = useAppSelector(getCarouselItems)
  const isLoading = useAppSelector(getIsLoading)
  const formModalVisible = useAppSelector(getModalVisibility(ModalName.CarouselItem))

  useEffect(() => {
    dispatch(LoginBrandingActions.getCarouselItems({ applicationId }))
    // Rời khỏi application này (đóng modal, hoặc Edit sang application khác) -> dọn list, không để lộ
    // carousel của application cũ ra application mới trong khoảnh khắc trước khi fetch mới trả về.
    return () => {
      dispatch(LoginBrandingActions.resetCarouselItems())
    }
  }, [applicationId])

  const sortedItems = [...items].sort((a, b) => a.displayOrder - b.displayOrder || a.id - b.id)

  const handleAddClick = () => {
    dispatch(LoginBrandingActions.setItemSelected(undefined))
    dispatch(LoginBrandingActions.setModalVisibility({ name: ModalName.CarouselItem, visible: true }))
  }

  const handleEditClick = (item: ILoginCarouselItem) => {
    dispatch(LoginBrandingActions.setItemSelected(item))
    dispatch(LoginBrandingActions.setModalVisibility({ name: ModalName.CarouselItem, visible: true }))
  }

  const handleDeleteClick = (item: ILoginCarouselItem) => {
    confirm({
      title: t('admin.confirmDeleteCarouselItem', { title: item.title }),
      icon: <ExclamationCircleFilled />,
      okText: t('admin.delete'),
      okType: 'danger',
      cancelText: t('cancel'),
      onOk() {
        dispatch(LoginBrandingActions.deleteCarouselItem({ applicationId, itemId: item.id }))
      },
    })
  }

  const handleToggleActive = (item: ILoginCarouselItem, isActive: boolean) => {
    dispatch(LoginBrandingActions.setCarouselItemActive({ applicationId, itemId: item.id, isActive }))
  }

  // Đổi thứ tự bằng cách hoán đổi DisplayOrder với item liền kề (2 lần PATCH) — không cần kéo-thả
  // (drag-and-drop) vì số lượng slide thường rất ít, up/down đơn giản là đủ dùng.
  const swapDisplayOrder = (item: ILoginCarouselItem, otherItem: ILoginCarouselItem) => {
    dispatch(
      LoginBrandingActions.setCarouselItemDisplayOrder({
        applicationId,
        itemId: item.id,
        displayOrder: otherItem.displayOrder,
      }),
    )
    dispatch(
      LoginBrandingActions.setCarouselItemDisplayOrder({
        applicationId,
        itemId: otherItem.id,
        displayOrder: item.displayOrder,
      }),
    )
  }

  const handleMoveUp = (index: number) => {
    if (index > 0) swapDisplayOrder(sortedItems[index], sortedItems[index - 1])
  }

  const handleMoveDown = (index: number) => {
    if (index < sortedItems.length - 1) swapDisplayOrder(sortedItems[index], sortedItems[index + 1])
  }

  const nextDisplayOrder = sortedItems.length > 0
    ? Math.max(...sortedItems.map(i => i.displayOrder)) + 1
    : 0

  return (
    <div>
      {formModalVisible && (
        <LoginCarouselItemFormModal applicationId={applicationId} nextDisplayOrder={nextDisplayOrder} />
      )}

      <Space align="center" style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }}>
        <Typography.Text strong>{t('admin.loginBranding')}</Typography.Text>
        <Button size="small" type="primary" icon={<PlusCircleOutlined />} onClick={handleAddClick}>
          {t('admin.addCarouselItem')}
        </Button>
      </Space>

      <Spin spinning={isLoading}>
        {sortedItems.length === 0 ? (
          <NoData content={t('admin.usingDefaultCarousel')} />
        ) : (
          <List
            bordered
            dataSource={sortedItems}
            renderItem={(item, index) => (
              <List.Item
                key={item.id}
                actions={[
                  <ArrowUpOutlined
                    key="up"
                    style={{ color: index === 0 ? '#d9d9d9' : undefined, cursor: index === 0 ? 'not-allowed' : 'pointer' }}
                    onClick={() => handleMoveUp(index)}
                  />,
                  <ArrowDownOutlined
                    key="down"
                    style={{
                      color: index === sortedItems.length - 1 ? '#d9d9d9' : undefined,
                      cursor: index === sortedItems.length - 1 ? 'not-allowed' : 'pointer',
                    }}
                    onClick={() => handleMoveDown(index)}
                  />,
                  <Switch
                    key="active"
                    size="small"
                    checked={item.isActive}
                    onChange={checked => handleToggleActive(item, checked)}
                  />,
                  <EditOutlined key="edit" style={{ color: '#1890ff' }} onClick={() => handleEditClick(item)} />,
                  <DeleteOutlined key="delete" style={{ color: 'red' }} onClick={() => handleDeleteClick(item)} />,
                ]}
              >
                <List.Item.Meta
                  avatar={
                    item.backgroundImageUrl ? (
                      <img
                        src={resolveIdentityAssetUrl(item.backgroundImageUrl)}
                        alt={item.title}
                        style={{
                          width: 64, height: 40, objectFit: 'cover', borderRadius: 4, border: '1px solid #f0f0f0',
                        }}
                      />
                    ) : undefined
                  }
                  title={item.title}
                  description={item.subtitle || undefined}
                />
              </List.Item>
            )}
          />
        )}
      </Spin>
    </div>
  )
}
